/* ===================================================================
   THE FLOAT CLUB — fc-sticky.js

   Two jobs.

   1. WHEN the buy bar appears. On mobile it used to sit over the hero
      from the first pixel: competing with the help bubble, and asking
      for the sale before the visitor had seen what they were buying.
      It now waits until they are past the two video sections, which is
      the moment they know what it is.

   2. WHETHER it stays. A bar that cannot be dismissed is a bar that
      covers the size chart while you are trying to read it. There is a
      handle on it now: pull it down and it folds away to a small round
      arrow control, tap it and the bar comes back. The choice is
      remembered, so it does not reappear on every page.

   The position is written inline rather than toggled through a class.
   A class plus a stylesheet rule leaves the cascade to decide, and the
   cascade was being raced by the transition: the same element measured
   translated and untranslated in the same state depending on when it
   was read. An inline transform has nothing to lose to.
=================================================================== */
(function (global) {
  "use strict";

  var bar = document.querySelector('[data-m="sticky-bar"]');
  if (!bar) return;

  var mq = window.matchMedia("(max-width: 820px)");
  var HIDDEN = "translateY(125%)";
  var GAP = 14;      /* clearance between the help launcher and whatever is under it */
  var REST = 20;     /* where the launcher sits with nothing beneath it */
  var KEY = "fc_bar_collapsed";

  var revealed = null;                 /* null so the first pass always paints */
  var collapsed = false;
  try { collapsed = localStorage.getItem(KEY) === "1"; } catch (e) {}

  var tab = null, handle = null;

  /* ---------------------------------------------------------------
     Chrome: the pull-down handle on the bar, and the tab it folds to
  --------------------------------------------------------------- */
  function chrome() {
    var s = document.createElement("style");
    s.textContent = [
      '[data-m="sticky-bar"]{transition:transform .34s cubic-bezier(.2,.8,.2,1)}',
      "#fc-help{transition:bottom .34s cubic-bezier(.2,.8,.2,1)}",

      /* The bar gets its own strip along the top for the handle. Without
         it the handle sat on the centre size chip and swallowed the top
         of that chip's tap target — elementFromPoint over the M chip
         returned the handle, not the chip. */
      '[data-m="sticky-bar"]{padding-top:30px !important}',
      "@media(max-width:820px){",
      '[data-m="sticky-bar"]{padding-top:27px !important}}',

      "#fc-bar-handle{position:absolute;top:0;left:0;right:0;height:26px;border:0;padding:0;",
      "cursor:pointer;background:transparent;display:flex;align-items:center;justify-content:center;gap:7px;",
      "color:rgba(234,246,248,.5);transition:color .18s ease}",
      "#fc-bar-handle i{display:block;width:34px;height:4px;border-radius:99px;",
      "background:currentColor;transition:width .18s ease}",
      "#fc-bar-handle:hover{color:rgba(234,246,248,.92)}",
      "#fc-bar-handle:hover i{width:42px}",
      "#fc-bar-handle svg{opacity:.9}",
      "#fc-bar-handle:focus-visible{outline:2px solid #FFC53D;outline-offset:-3px;border-radius:8px}",

      /* the folded state: a plain round arrow control, same styling family
         as the help launcher (dark glass, hairline border, blur) — not a
         second CTA. No price, no "BUY" label: it only says "show the bar".
         Bottom-LEFT, so it never shares a corner with the help launcher. */
      "#fc-bar-tab{position:fixed;left:16px;bottom:calc(20px + env(safe-area-inset-bottom));z-index:39;",
      "display:flex;align-items:center;justify-content:center;width:48px;height:48px;padding:0;border:0;",
      "border-radius:999px;cursor:pointer;background:rgba(3,24,31,.92);backdrop-filter:blur(8px);color:#EAF6F8;",
      "border:1px solid rgba(255,255,255,.16);box-shadow:0 14px 34px rgba(3,24,31,.4);",
      "opacity:0;pointer-events:none;transform:translateY(12px);",
      "transition:opacity .24s ease,transform .28s cubic-bezier(.2,.8,.2,1)}",
      "#fc-bar-tab.on{opacity:1;pointer-events:auto;transform:none}",
      "#fc-bar-tab:hover{background:#03181F}",
      "#fc-bar-tab:focus-visible{outline:3px solid #FFC53D;outline-offset:3px}",
      "#fc-bar-tab svg{opacity:.9}",

      "@media(prefers-reduced-motion:reduce){",
      '[data-m="sticky-bar"],#fc-bar-tab,#fc-help{transition:none !important}}'
    ].join("");
    document.head.appendChild(s);

    if (getComputedStyle(bar).position === "static") bar.style.position = "relative";

    handle = document.createElement("button");
    handle.type = "button";
    handle.id = "fc-bar-handle";
    handle.setAttribute("aria-label", "Hide the buy bar");
    handle.title = "Hide the buy bar";
    /* a chevron beside the grab pill, so it reads as "this folds away"
       rather than as a decorative line */
    handle.innerHTML = "<i></i>" +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.8" stroke-linecap="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
    handle.addEventListener("click", function () { setCollapsed(true); });
    bar.appendChild(handle);

    tab = document.createElement("button");
    tab.type = "button";
    tab.id = "fc-bar-tab";
    tab.setAttribute("aria-label", "Show the buy bar");
    /* icon only: an up chevron, nothing else. Nothing here displays the
       price, so nothing needs to track it any more. */
    tab.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="m6 15 6-6 6 6"/></svg>';
    tab.addEventListener("click", function () { setCollapsed(false); });
    document.body.appendChild(tab);
  }

  function setCollapsed(v) {
    collapsed = v;
    try { localStorage.setItem(KEY, v ? "1" : "0"); } catch (e) {}
    paint();
    /* move focus to whichever control is now on screen, so a keyboard
       user is not left holding a button that just disappeared */
    if (v) { if (tab) tab.focus(); }
    else if (handle) handle.focus();
  }

  /* ---------------------------------------------------------------
     When the bar is allowed to show at all
  --------------------------------------------------------------- */
  function trigger() {
    var club = document.getElementById("club");
    if (!club) return window.innerHeight * 1.6;
    return club.getBoundingClientRect().bottom + window.scrollY;
  }

  /* fc-help builds its DOM on DOMContentLoaded, so a reference captured
     when this file was evaluated is null and the launcher never moves.
     That is what put it on top of the bar. Looked up on every call. */
  function launcher() { return document.getElementById("fc-help"); }

  function paint() {
    var showBar = revealed && !collapsed;
    var showTab = revealed && collapsed;

    bar.style.transform = showBar ? "" : HIDDEN;
    bar.setAttribute("aria-hidden", showBar ? "false" : "true");
    if (tab) tab.classList.toggle("on", !!showTab);

    var help = launcher();
    if (!help) return;
    /* the arrow tab lives bottom-left and the help launcher bottom-right,
       so the launcher only needs to climb above the full-width bar */
    var clear = REST;
    if (showBar) clear = (Math.round(bar.getBoundingClientRect().height) || 126) + GAP;
    help.style.bottom = "calc(" + clear + "px + env(safe-area-inset-bottom))";
  }

  function update() {
    /* Same reveal rule on every screen size: the bar waits until the
       visitor is past the two video sections. Desktop used to show it
       from the first pixel, which parked it on top of the hero copy —
       the vest description was cut mid-sentence behind it. */
    var next = (window.scrollY + window.innerHeight * 0.9) > trigger();
    if (next !== revealed) { revealed = next; paint(); }
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; update(); });
  }

  /* hide it straight away so it cannot flash over the hero on load */
  if (mq.matches) bar.style.transform = HIDDEN;
  chrome();

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
  if (mq.addEventListener) mq.addEventListener("change", update);
  document.addEventListener("DOMContentLoaded", update);
  window.addEventListener("load", update);
  update();

  global.FC_STICKY = {
    update: update,
    collapse: function () { setCollapsed(true); },
    expand: function () { setCollapsed(false); },
    state: function () { return { revealed: revealed, collapsed: collapsed }; }
  };
})(window);
